import { FunctionComponent, memo, useContext } from "react";
import Modal, { ModalContent, ModalHeader, useModal } from "@edgetier/modal";
import { axios } from "utilities/axios";
import toast from "react-hot-toast";
import { faCheck, faRedoAlt } from "@fortawesome/pro-solid-svg-icons";
import { EllipsisMenuContext, EllipsisMenuItem } from "@edgetier/client-components";
import { getTimeToRead } from "@edgetier/utilities";
import { getServerError } from "utilities/get-server-error";
import { Button } from "@edgetier/components";
import { IUser, Route } from "@edgetier/watchtower-types";
import { IProps } from "./user-reinvite-action.types";
import "./user-reinvite-action.scss";

/**
 * A menu action, when clicked open a confirmation modal to resend the invite to user
 * @param props.user The user not activated.
 */
const UserReinviteAction: FunctionComponent<IProps> = ({ user }) => {
    const { lockOpen, unlock } = useContext(EllipsisMenuContext);
    const { closeModal, openModal, modalProps } = useModal({ onCloseModal: unlock });

    /**
     * POST request to send the invite to the user
     */
    const sendInvite = async (user: IUser) => {
        try {
            await axios.post(Route.UserInvite.replace(":userId", user.userId));
            const successMessage = `Successfully re-sent the invite to ${user.firstName} ${user.surname}`;
            toast.success(successMessage, { duration: getTimeToRead(successMessage, 1000) });
        } catch (error) {
            toast.error(getServerError(error));
        } finally {
            closeModal();
        }
    };

    return (
        <>
            <Modal {...modalProps}>
                <ModalHeader>
                    <h2>Resend Invite</h2>
                </ModalHeader>
                <ModalContent>
                    <div className="user-reinvite-action">
                        <p>
                            Are you sure you want to send the invite again to &ldquo;{user.firstName} {user.surname}
                            &rdquo;?
                        </p>
                    </div>
                    <div className="user-reinvite-action__buttons">
                        <Button icon={faCheck} styleName="positive" onClick={() => sendInvite(user)}>
                            Send Invite
                        </Button>
                    </div>
                </ModalContent>
            </Modal>
            <EllipsisMenuItem
                closeAfterClick={false}
                icon={faRedoAlt}
                onClick={() => {
                    lockOpen();
                    openModal();
                }}
            >
                Resend Invite
            </EllipsisMenuItem>
        </>
    );
};

export default memo(UserReinviteAction);
